import { useCallback, useState } from "react";

import SearchAndControlsView from "./SearchAndControlsView";
import { useKanbanColors } from "@/features/player-kanban/hooks/useKanbanColors";

/**
 * Props for SearchAndControlsContainer component
 */
interface SearchAndControlsContainerProps {
  searchQuery: string;
  sortLabel: string;
  activeFilterCount?: number;
  onSearchChange: (query: string) => void;
  onSortChange: () => void;
  onFilterPress?: () => void;
}

/**
 * Container component for SearchAndControls - manages search and filter control logic
 * @param root0 - The component props
 * @param root0.searchQuery - Current search query string for filtering players
 * @param root0.sortLabel - Label describing the current sort configuration
 * @param root0.activeFilterCount - Number of active filters currently applied (optional)
 * @param root0.onSearchChange - Callback invoked when the search query changes
 * @param root0.onSortChange - Callback invoked to trigger sort changes
 * @param root0.onFilterPress - Callback invoked when the filter button is pressed (optional)
 */
const SearchAndControlsContainer = ({
  searchQuery,
  sortLabel,
  activeFilterCount,
  onSearchChange,
  onSortChange,
  onFilterPress,
}: SearchAndControlsContainerProps) => {
  const colors = useKanbanColors();
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const handleCloseAllDropdowns = useCallback(() => {
    setShowFilterDropdown(false);
    setShowSortDropdown(false);
  }, []);

  const handleFilterDropdownToggle = useCallback(() => {
    setShowFilterDropdown(prev => !prev);
    setShowSortDropdown(false);
  }, []);

  const handleSortDropdownToggle = useCallback(() => {
    setShowSortDropdown(prev => !prev);
    setShowFilterDropdown(false);
  }, []);

  return (
    <SearchAndControlsView
      searchQuery={searchQuery}
      sortLabel={sortLabel}
      activeFilterCount={activeFilterCount}
      showFilterDropdown={showFilterDropdown}
      showSortDropdown={showSortDropdown}
      colors={colors}
      onSearchChange={onSearchChange}
      onSortChange={onSortChange}
      onFilterPress={onFilterPress}
      onCloseAllDropdowns={handleCloseAllDropdowns}
      onFilterDropdownToggle={handleFilterDropdownToggle}
      onSortDropdownToggle={handleSortDropdownToggle}
    />
  );
};

export default SearchAndControlsContainer;
